import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { exerciseLibrary } from '../../services/api';
import MediaManager from './MediaManager';
import ExerciseMediaUpload from './ExerciseMediaUpload';
import MusclePicker from './MusclePicker';
import SubstitutesEditor from './SubstitutesEditor';
import { Chip, ChipsInput, ConfirmDialog, Field, ListEditor, Modal, errorList } from './shared';

/*
 * Criar ou editar um exercício. Uma janela com secções (Dados, Músculos, Equipamento, Execução,
 * Substitutos, Imagens); o servidor valida tudo e devolve a lista completa de problemas.
 *
 * Quando o exercício vem do catálogo, editar a definição tira-o às actualizações do catálogo (podes
 * repô-lo); desativar não conta. As imagens não precisam de "Guardar": cada operação é imediata.
 */

const SECTIONS = [
  ['dados', 'Dados'], ['musculos', 'Músculos'], ['equipamento', 'Equipamento'],
  ['execucao', 'Execução'], ['substitutos', 'Substitutos'], ['imagens', 'Imagens'],
];

const emptyForm = () => ({
  name: '', nameEn: '', aliases: [], description: '',
  groupCode: '', level: 'beginner', mechanic: '', force: '', movementPattern: '', trackingType: 'weight-reps', exerciseType: 'strength', variantOfSlug: '',
  steps: [], mistakes: [], tip: '',
  defaultSets: '3', defaultRepsMin: '10', defaultRepsMax: '12', defaultRestSeconds: '90',
  primary: [], secondary: [], equipmentLinks: [], substitutes: [], isActive: true,
});

const toForm = (d) => ({
  name: d.name || '', nameEn: d.nameEn || '', aliases: [...(d.aliases || [])], description: d.description || '',
  groupCode: d.groupCode || '', level: d.level || '', mechanic: d.mechanic || '', force: d.force || '',
  movementPattern: d.movementPattern || '', trackingType: d.trackingType || 'weight-reps',
  exerciseType: d.exerciseType || 'strength', variantOfSlug: d.variantOfSlug || '',
  steps: [...(d.steps || [])], mistakes: [...(d.mistakes || [])], tip: d.tip || '',
  defaultSets: d.defaultSets == null ? '' : String(d.defaultSets),
  defaultRepsMin: d.defaultRepsMin == null ? '' : String(d.defaultRepsMin),
  defaultRepsMax: d.defaultRepsMax == null ? '' : String(d.defaultRepsMax),
  defaultRestSeconds: d.defaultRestSeconds == null ? '' : String(d.defaultRestSeconds),
  primary: (d.muscles || []).filter((m) => m.role === 'primary').map((m) => m.id),
  secondary: (d.muscles || []).filter((m) => m.role === 'secondary').map((m) => m.id),
  equipmentLinks: (d.equipmentLinks || []).map((e) => ({ equipmentId: e.id, isPrimary: e.isPrimary })),
  substitutes: (d.substitutes || []).map((s) => ({
    exerciseId: s.exerciseId, name: s.name, kind: s.kind, note: s.note || '', priority: s.priority,
    imageUrl: s.imageUrl, available: s.available, isActive: s.isActive,
  })),
  isActive: d.isActive !== false,
});

const toInt = (value) => {
  const text = String(value ?? '').trim();
  if (text === '') return null;
  const n = Number(text);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

const toPayload = (f) => ({
  name: f.name.trim(),
  nameEn: f.nameEn.trim(),
  aliases: f.aliases,
  description: f.description.trim(),
  groupCode: f.groupCode,
  level: f.level,
  mechanic: f.mechanic,
  force: f.force,
  movementPattern: f.movementPattern,
  trackingType: f.trackingType,
  exerciseType: f.exerciseType,
  variantOfSlug: f.variantOfSlug.trim(),
  steps: f.steps.map((s) => s.trim()).filter(Boolean),
  mistakes: f.mistakes.map((s) => s.trim()).filter(Boolean),
  tip: f.tip.trim(),
  defaultSets: toInt(f.defaultSets),
  defaultRepsMin: toInt(f.defaultRepsMin),
  defaultRepsMax: toInt(f.defaultRepsMax),
  defaultRestSeconds: toInt(f.defaultRestSeconds),
  primaryMuscles: f.primary,
  secondaryMuscles: f.secondary,
  equipmentLinks: f.equipmentLinks,
  substitutes: f.substitutes.map((s) => ({
    exerciseId: s.exerciseId, kind: s.kind, note: s.note || '', priority: s.priority ?? undefined,
  })),
  isActive: f.isActive,
});

export default function ExerciseEditor({ exerciseId, meta, machines, onClose, onSaved, onDeleted }) {
  const [id, setId] = useState(exerciseId);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [initial, setInitial] = useState(() => JSON.stringify(emptyForm()));
  const [loading, setLoading] = useState(!!exerciseId);
  const [loadError, setLoadError] = useState(null);
  const [section, setSection] = useState('dados');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [confirm, setConfirm] = useState(null);   // 'reset' | 'delete' | 'discard'
  const [machineFilter, setMachineFilter] = useState('');
  const bodyRef = useRef(null);

  const canEdit = meta.canManage;
  const dirty = JSON.stringify(form) !== initial;

  const adopt = useCallback((d) => {
    const next = toForm(d);
    setDetail(d);
    setForm(next);
    setInitial(JSON.stringify(next));
  }, []);

  useEffect(() => {
    if (!exerciseId) return undefined;
    let alive = true;
    setLoading(true);
    exerciseLibrary.get(exerciseId)
      .then(({ data }) => { if (alive) adopt(data); })
      .catch((e) => { if (alive) setLoadError(errorList(e, 'Não foi possível carregar o exercício.').join(' ')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [exerciseId, adopt]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const close = () => (dirty && canEdit ? setConfirm('discard') : onClose());

  const save = async () => {
    setErrors([]);
    if (!form.name.trim()) {
      setErrors(['O nome é obrigatório.']);
      setSection('dados');
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      const { data } = id ? await exerciseLibrary.update(id, payload) : await exerciseLibrary.create(payload);
      setId(data.id);
      adopt(data);
      onSaved(data);
      toast.success(id ? 'Exercício guardado' : 'Exercício criado');
    } catch (e) {
      setErrors(errorList(e));
      bodyRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const { data } = await exerciseLibrary.update(id, { isActive: !detail.isActive });
      setDetail(data);
      setForm((f) => ({ ...f, isActive: data.isActive }));
      setInitial((i) => JSON.stringify({ ...JSON.parse(i), isActive: data.isActive }));
      onSaved(data);
      toast.success(data.isActive ? 'Exercício ativado' : 'Exercício desativado');
    } catch (e) {
      setErrors(errorList(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await exerciseLibrary.reset(id);
      adopt(data);
      onSaved(data);
      toast.success('Exercício reposto como no catálogo');
      setConfirm(null);
    } catch (e) {
      setErrors(errorList(e));
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await exerciseLibrary.remove(id);
      toast.success('Exercício apagado');
      onDeleted(id);
    } catch (e) {
      setErrors(errorList(e));
      setConfirm(null);
      setSaving(false);
    }
  };

  // As máquinas que o exercício já tinha (as GRAVADAS) vão para cima: num catálogo de dezenas, a do
  // exercício estava a uma rolagem de distância. Só as gravadas — se subissem à medida que se marcam,
  // a linha fugia debaixo do dedo.
  const filteredMachines = useMemo(() => {
    const terms = machineFilter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const saved = new Set((detail?.equipmentLinks || []).map((l) => l.id));
    const matching = machines.filter((m) => terms.every((t) => `${m.name} ${m.nameEn || ''} ${m.kindLabel} ${m.familyLabel}`.toLowerCase().includes(t)));
    return [...matching.filter((m) => saved.has(m.id)), ...matching.filter((m) => !saved.has(m.id))];
  }, [machines, machineFilter, detail]);

  const setMachine = (machineId, selected) => {
    let links = selected
      ? [...form.equipmentLinks, { equipmentId: machineId, isPrimary: form.equipmentLinks.length === 0 }]
      : form.equipmentLinks.filter((l) => l.equipmentId !== machineId);
    if (links.length > 0 && !links.some((l) => l.isPrimary)) links = links.map((l, i) => ({ ...l, isPrimary: i === 0 }));
    set({ equipmentLinks: links });
  };

  const setPrimaryMachine = (machineId) =>
    set({ equipmentLinks: form.equipmentLinks.map((l) => ({ ...l, isPrimary: l.equipmentId === machineId })) });

  const timeBased = form.trackingType === 'time';
  const title = id ? (detail ? detail.name : 'Exercício') : 'Novo exercício';

  const footer = canEdit ? (
    <>
      <div className="grow">
        {id && detail && (
          <button type="button" className="lib-btn" disabled={saving} onClick={toggleActive}>
            {detail.isActive ? 'Desativar' : 'Ativar'}
          </button>
        )}
        {id && detail?.source === 'catalog' && detail.isCustomized && (
          <button type="button" className="lib-btn" disabled={saving} onClick={() => setConfirm('reset')}>Repor do catálogo</button>
        )}
        {id && detail && detail.source !== 'catalog' && (
          <button type="button" className="lib-btn danger" disabled={saving} onClick={() => setConfirm('delete')}>Apagar</button>
        )}
      </div>
      <button type="button" className="lib-btn" disabled={saving} onClick={close}>Fechar</button>
      <button type="button" className="lib-btn primary" disabled={saving || (!dirty && !!id)} onClick={save}>
        {saving ? 'A guardar…' : id ? 'Guardar' : 'Criar exercício'}
      </button>
    </>
  ) : <button type="button" className="lib-btn" onClick={onClose}>Fechar</button>;

  return (
    <>
      <Modal title={title} onClose={close} footer={footer}>
        <div ref={bodyRef}>
          {loading && <div className="lib-loading" role="status">A carregar…</div>}
          {loadError && <div className="lib-notice bad" role="alert">{loadError}</div>}

          {!loading && !loadError && (
            <>
              {errors.length > 0 && (
                <div className="lib-notice bad" role="alert">
                  {errors.length === 1 ? errors[0] : <><strong>Corrija antes de guardar:</strong><ul>{errors.map((m) => <li key={m}>{m}</li>)}</ul></>}
                </div>
              )}

              {detail?.source === 'catalog' && (
                <div className={`lib-notice${detail.isCustomized ? ' warn' : ''}`}>
                  {detail.isCustomized
                    ? 'Exercício do catálogo, editado neste ginásio: o catálogo já não lhe toca. Pode repô-lo como o catálogo o define.'
                    : 'Exercício do catálogo. Se alterar a definição, deixa de receber as atualizações do catálogo (pode repô-lo depois). Desativar não conta.'}
                </div>
              )}
              {detail && detail.available === false && (
                <div className="lib-notice warn">Falta, neste ginásio, uma máquina de que este exercício precisa — não será sugerido como alternativa.</div>
              )}

              <div className="lib-sections" role="tablist" aria-label="Secções do exercício">
                {SECTIONS.map(([key, label]) => (
                  <button key={key} type="button" role="tab" className="lib-section-btn" aria-selected={section === key} onClick={() => setSection(key)}>
                    {label}
                  </button>
                ))}
              </div>

              {section === 'dados' && (
                <div role="tabpanel">
                  <div className="lib-row">
                    <Field label="Nome *" htmlFor="ex-name">
                      <input id="ex-name" className="lib-input" value={form.name} maxLength={100} disabled={!canEdit} required
                        onChange={(e) => set({ name: e.target.value })} aria-invalid={errors.length > 0 && !form.name.trim()} />
                    </Field>
                    <Field label="Nome em inglês" htmlFor="ex-nameen">
                      <input id="ex-nameen" className="lib-input" value={form.nameEn} maxLength={120} disabled={!canEdit} onChange={(e) => set({ nameEn: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Outros nomes" hint="Como os atletas lhe chamam (português ou inglês): Enter para acrescentar. Entram na pesquisa.">
                    <ChipsInput values={form.aliases} onChange={(aliases) => set({ aliases })} placeholder="Ex.: leg press" ariaLabel="Outros nomes" />
                  </Field>
                  <Field label="Descrição" htmlFor="ex-desc">
                    <textarea id="ex-desc" className="lib-textarea" value={form.description} maxLength={1000} disabled={!canEdit}
                      onChange={(e) => set({ description: e.target.value })} />
                  </Field>

                  <div className="lib-row cols-3">
                    <Field label="Grupo muscular" htmlFor="ex-group">
                      <select id="ex-group" className="lib-select" value={form.groupCode} disabled={!canEdit} onChange={(e) => set({ groupCode: e.target.value })}>
                        <option value="">— (deduzir do músculo principal)</option>
                        {meta.groups.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Nível" htmlFor="ex-level">
                      <select id="ex-level" className="lib-select" value={form.level} disabled={!canEdit} onChange={(e) => set({ level: e.target.value })}>
                        <option value="">—</option>
                        {meta.levels.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Mecânica" htmlFor="ex-mech" hint="Composto usa várias articulações; isolamento, uma.">
                      <select id="ex-mech" className="lib-select" value={form.mechanic} disabled={!canEdit} onChange={(e) => set({ mechanic: e.target.value })}>
                        <option value="">—</option>
                        {meta.mechanics.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Força" htmlFor="ex-force">
                      <select id="ex-force" className="lib-select" value={form.force} disabled={!canEdit} onChange={(e) => set({ force: e.target.value })}>
                        <option value="">—</option>
                        {meta.forces.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Padrão de movimento" htmlFor="ex-pattern">
                      <select id="ex-pattern" className="lib-select" value={form.movementPattern} disabled={!canEdit} onChange={(e) => set({ movementPattern: e.target.value })}>
                        <option value="">—</option>
                        {meta.patterns.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Tipo de exercício" htmlFor="ex-type">
                      <select id="ex-type" className="lib-select" value={form.exerciseType} disabled={!canEdit} onChange={(e) => set({ exerciseType: e.target.value })}>
                        {(meta.exerciseTypes || []).map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Variante de" htmlFor="ex-variant" hint="O endereço (slug) do exercício base, se este for uma variante dele. Deixe vazio se não for.">
                      <input id="ex-variant" className="lib-input" maxLength={120} value={form.variantOfSlug} disabled={!canEdit} onChange={(e) => set({ variantOfSlug: e.target.value })} />
                    </Field>
                    <Field label="O que se regista" htmlFor="ex-track" hint="Em «Tempo», as repetições são segundos.">
                      <select id="ex-track" className="lib-select" value={form.trackingType} disabled={!canEdit} onChange={(e) => set({ trackingType: e.target.value })}>
                        {meta.trackingTypes.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                      </select>
                    </Field>
                  </div>

                  <h3 style={{ fontSize: '0.95rem', margin: '4px 0 8px' }}>Prescrição por omissão</h3>
                  <div className="lib-row cols-4">
                    <Field label="Séries" htmlFor="ex-sets"><input id="ex-sets" className="lib-input" type="number" inputMode="numeric" min={1} max={10} value={form.defaultSets} disabled={!canEdit} onChange={(e) => set({ defaultSets: e.target.value })} /></Field>
                    <Field label={timeBased ? 'Mínimo (s)' : 'Repetições (mín.)'} htmlFor="ex-rmin"><input id="ex-rmin" className="lib-input" type="number" inputMode="numeric" min={1} value={form.defaultRepsMin} disabled={!canEdit} onChange={(e) => set({ defaultRepsMin: e.target.value })} /></Field>
                    <Field label={timeBased ? 'Máximo (s)' : 'Repetições (máx.)'} htmlFor="ex-rmax"><input id="ex-rmax" className="lib-input" type="number" inputMode="numeric" min={1} value={form.defaultRepsMax} disabled={!canEdit} onChange={(e) => set({ defaultRepsMax: e.target.value })} /></Field>
                    <Field label="Descanso (s)" htmlFor="ex-rest"><input id="ex-rest" className="lib-input" type="number" inputMode="numeric" min={0} max={600} value={form.defaultRestSeconds} disabled={!canEdit} onChange={(e) => set({ defaultRestSeconds: e.target.value })} /></Field>
                  </div>
                </div>
              )}

              {section === 'musculos' && (
                <div role="tabpanel">
                  {form.primary.length === 0 && <div className="lib-notice warn">Escolha pelo menos um músculo principal: é o que permite encontrar o exercício por músculo.</div>}
                  <MusclePicker muscles={meta.muscles} primary={form.primary} secondary={form.secondary} disabled={!canEdit}
                    onChange={({ primary, secondary }) => set({ primary, secondary })} />
                </div>
              )}

              {section === 'equipamento' && (
                <div role="tabpanel">
                  <div className="lib-notice">
                    Marque a(s) máquina(s) deste exercício e qual é a <strong>principal</strong> — a que o define. Os outros (banco, rack…)
                    são acessórios: se faltarem, o exercício continua possível.
                  </div>
                  <Field label="Procurar máquina" htmlFor="ex-machine-filter">
                    <input id="ex-machine-filter" className="lib-input" type="search" value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)} placeholder="Nome, tipo ou zona…" />
                  </Field>
                  {machines.length === 0 ? (
                    <div className="lib-empty">Ainda não há equipamento. Crie-o no separador «Equipamento».</div>
                  ) : (
                    <div className="lib-picklist" role="group" aria-label="Equipamento do exercício">
                      {filteredMachines.map((m) => {
                        const link = form.equipmentLinks.find((l) => l.equipmentId === m.id);
                        return (
                          <div key={m.id} className="lib-pick">
                            <input type="checkbox" id={`ex-m-${m.id}`} checked={!!link} disabled={!canEdit} style={{ width: 18, height: 18 }}
                              onChange={(e) => setMachine(m.id, e.target.checked)} />
                            <label htmlFor={`ex-m-${m.id}`}>
                              <span>
                                <span className={`lib-dot${m.isAvailable && m.isActive ? '' : ' off'}`} title={m.isAvailable && m.isActive ? 'O ginásio tem' : 'O ginásio não tem'} />
                                {m.name} <span className="lib-card-sub">· {m.kindLabel}</span>
                              </span>
                            </label>
                            {link ? (
                              <label className="lib-check" style={{ minHeight: 36 }}>
                                <input type="radio" name="primary-machine" checked={link.isPrimary} disabled={!canEdit} onChange={() => setPrimaryMachine(m.id)} />
                                Principal
                              </label>
                            ) : <span />}
                          </div>
                        );
                      })}
                      {filteredMachines.length === 0 && <div className="lib-hint" style={{ padding: 8 }}>Nenhuma máquina encontrada.</div>}
                    </div>
                  )}
                </div>
              )}

              {section === 'execucao' && (
                <div role="tabpanel">
                  <Field label="Passos de execução" hint="Até 8 passos, curtos e no imperativo (é o que o atleta lê durante o treino).">
                    <ListEditor items={form.steps} onChange={(steps) => set({ steps })} max={8} maxLength={300} addLabel="Acrescentar passo" placeholder="Ex.: Apoia as costas no encosto." ariaLabel="Passo" />
                  </Field>
                  <Field label="Erros comuns" hint="Até 6.">
                    <ListEditor items={form.mistakes} onChange={(mistakes) => set({ mistakes })} max={6} maxLength={200} addLabel="Acrescentar erro" placeholder="Ex.: Levantar a bacia do encosto." ariaLabel="Erro comum" />
                  </Field>
                  <Field label="Dica" htmlFor="ex-tip">
                    <input id="ex-tip" className="lib-input" value={form.tip} maxLength={500} disabled={!canEdit} onChange={(e) => set({ tip: e.target.value })} />
                  </Field>
                </div>
              )}

              {section === 'substitutos' && (
                <div role="tabpanel">
                  <SubstitutesEditor selfId={id} value={form.substitutes} kinds={meta.substituteKinds} disabled={!canEdit}
                    onChange={(substitutes) => set({ substitutes })} />
                </div>
              )}

              {section === 'imagens' && (
                <div role="tabpanel">
                  {id && detail ? (
                    <>
                      {/* A media única: um ficheiro que substitui o que o ginásio tinha. As imagens do catálogo ficam. */}
                      <ExerciseMediaUpload
                        api={exerciseLibrary}
                        exerciseId={id}
                        media={detail.media}
                        canEdit={canEdit}
                        onChange={(next) => {
                          if (next) { setDetail(next); onSaved(next); return; }
                          // Sem a resposta do servidor, a lista local perde os ficheiros do ginásio (o servidor apagou-os).
                          setDetail((d) => (d ? { ...d, media: (d.media || []).filter((m) => m.source !== 'upload') } : d));
                        }} />
                      <h3 style={{ fontSize: '0.95rem', margin: '16px 0 8px' }}>Galeria e validação</h3>
                      <MediaManager api={exerciseLibrary} ownerId={id} media={detail.media} limits={meta.media} canEdit={canEdit}
                        demo={detail.demo}
                        onChange={(media) => { setDetail((d) => ({ ...d, media, imageUrl: media.find((m) => m.isPrimary)?.url ?? d.imageUrl })); onSaved({ ...detail, media, imageUrl: media.find((m) => m.isPrimary)?.url ?? detail.imageUrl }); }}
                        onDetail={(next) => { setDetail(next); onSaved(next); }} />
                    </>
                  ) : (
                    <div className="lib-notice">Guarde o exercício para lhe poder carregar imagens.</div>
                  )}
                </div>
              )}

              {detail && (
                <div className="lib-chips" style={{ marginTop: 16 }}>
                  <Chip>{detail.slug}</Chip>
                  {detail.catalogVersion != null && <Chip>Catálogo v{detail.catalogVersion}</Chip>}
                  {!detail.isActive && <Chip tone="bad">Desativado</Chip>}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {confirm === 'discard' && (
        <ConfirmDialog title="Descartar alterações?" message="Tem alterações por guardar. Se fechar agora, perdem-se." confirmLabel="Descartar" danger
          onConfirm={onClose} onCancel={() => setConfirm(null)} />
      )}
      {confirm === 'reset' && (
        <ConfirmDialog title="Repor do catálogo" busy={saving} confirmLabel="Repor"
          message="O exercício volta a ser como o catálogo o define (nome, descrição, músculos, equipamento e substitutos). As imagens que carregou ficam."
          onConfirm={reset} onCancel={() => setConfirm(null)} />
      )}
      {confirm === 'delete' && (
        <ConfirmDialog title="Apagar exercício" busy={saving} confirmLabel="Apagar" danger
          message="O exercício, as suas relações e as imagens que carregou são apagados. Se estiver num plano de treino, o servidor recusa — desative-o."
          onConfirm={remove} onCancel={() => setConfirm(null)} />
      )}
    </>
  );
}
